import React from "react";
import { Select, Tooltip } from "antd";
import type { TooltipPlacement } from "antd/es/tooltip";
import { useField } from "formik";
import { ErrorMessage, useFormikContext } from "formik";
import { styled } from "styled-components";
import { CaretDownOutlined } from "@ant-design/icons";
import { ConfigProvider } from "antd";

interface SelectFieldProps extends React.ComponentProps<typeof Select> {
  name: string;
  label?: string;
  options: { value: string; label: string }[];
  value: string | string[];
  placeholder?: string;
  size?: "small" | "middle" | "large";
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  isRequired?: boolean;
  iconHide?: boolean;
  height?: string;
  disable?: boolean;
  customStyle?: string;
  labelMb?: number | string;
  labelWeight?: number | string;
  labelSize?: number | string;
  showHelp?: boolean;
  helpText?: string;
  helpPlacement?: TooltipPlacement;
}

const FieldContainer = styled.div<{ $error?: boolean, $height?: string }>`
  font-family: "Poppins", sans-serif;

  .ant-select-single {
    font-size: 10px;
    // height: 38px;
    align-items: center;
  }

  .ant-select-selection-item {
    font-size: 12px !important;
  }

  .ant-select-multiple .ant-select-selection-item {
    font-size: 12px !important; 
  }

  .ant-select-selection-placeholder {
    color: #999 !important;
    font-family: "Poppins", sans-serif !important;
    font-size: 12px !important;
  }

  .ant-select-multiple .ant-select-selector {
    max-height: 200px !important;
    height: ${({ $height }) => ($height !== undefined ? $height : "auto")} !important;
    // height: 38px !important;
    overflow: auto !important;
    display: flex;
    align-items: center;
    flex-wrap: nowrap !important;
    white-space: nowrap;
    border-radius: 5px;
  }

  .ant-select-selector {
    border-color: ${({ $error }) => ($error ? "red" : "#d9d9d9")} !important;
  }

  .ant-select-multiple .ant-select-selection-overflow {
    padding: ${({ $height }) => ($height !== undefined ? "10px" : "0")} !important;
    flex-wrap: nowrap !important;
    overflow: hidden !important;
  }

  .ant-select-multiple .ant-select-selection-item {
    max-width: 100px;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  .ant-select-focused .ant-select-selector,
  .ant-select-open .ant-select-selector {
    border-radius: 5px !important; /* 🔥 Remove border-radius on focus/open */
    box-shadow: none !important;
  }

  .ant-select-multiple .ant-select-selection-search {
    flex: none;
    outline: none !important;
    box-shadow: none !important;
  }
`;

const LabelRow = styled.div<{ $mb?: number | string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ $mb }) => ($mb !== undefined ? $mb : "4px")};
`;

const StyledLabel = styled.label<{
  $weight?: number | string;
  $fontSize?: number | string;
}>`
  display: block;
  font-weight: ${({ $weight }) => ($weight !== undefined ? $weight : 600)};
  font-size: ${({ $fontSize }) =>
    $fontSize !== undefined
      ? typeof $fontSize === "number"
        ? `${$fontSize}px`
        : $fontSize
      : "14px"};
`;

const StyledError = styled.div`
  color: red;
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const HelpIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1px solid #cfcfcf;
  border-radius: 50%;
  font-size: 11px;
  color: #8b8b8b;
  cursor: pointer;
  user-select: none;
`;

const SelectFieldWithSearch: React.FC<SelectFieldProps> = ({
  name,
  label,
  options,
  value,
  placeholder,
  height,
  size = "middle",
  isRequired = false,
  iconHide = false,
  onChange,
  disable = false,
  customStyle = "",
  labelMb,
  labelWeight,
  labelSize = "14px",
  showHelp = false,
  helpText,
  helpPlacement = "topRight",
  ...props
}) => {
  const [field, meta, helpers] = useField(name);
  const hasError = meta.touched && !!meta.error;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { setFieldValue, setTouched, setFieldTouched } = useFormikContext();

  const handleChange = (valueArg: string | string[]) => {
    if (props.mode === "tags" && Array.isArray(valueArg)) {
      const cleaned = valueArg
        .map((v) => (typeof v === "string" ? v.replace(/\s+/g, " ").trim() : v))
        .filter((v) => v !== "");

      helpers.setValue(cleaned);
      setFieldValue(name, cleaned);
      if (onChange) {
        onChange?.({
          target: { value: cleaned },
        } as unknown as React.ChangeEvent<HTMLSelectElement>);
      }
    } else {
      helpers.setValue(valueArg);
      setFieldValue(name, valueArg);
      if (onChange) {
        onChange?.({
          target: { value: valueArg },
        } as React.ChangeEvent<HTMLSelectElement>);
      }
    }
  };

  return (
    <FieldContainer $error={hasError} $height={height}>
      {label && (
        <LabelRow $mb={labelMb}>
          <StyledLabel
            htmlFor={name}
            $weight={labelWeight}
            $fontSize={labelSize}
          >
            {label}
            {isRequired && (
              <span style={{ color: "red", fontSize: "15px" }}>*</span>
            )}
          </StyledLabel>

          {showHelp && !!helpText && (
            <Tooltip
              title={helpText}
              placement={helpPlacement}
              zIndex={2000}
              // getPopupContainer={(trigger) => trigger?.parentElement || document.body}
            >
              <HelpIcon aria-label="help">?</HelpIcon>
            </Tooltip>
          )}
        </LabelRow>
      )}
      <ConfigProvider
        theme={{
          components: {
            Select: {
              borderRadius: 8,
              optionSelectedBg: "#f0f0f0",
              optionSelectedColor: "#000000",
              optionActiveBg: "#f0f0f0",
            },
          },
        }}
      >
        <Select
          {...field}
          {...props}
          showSearch
          placeholder={placeholder || "Select an option..."}
          optionFilterProp="label"
          // value={value}
          value={
            value !== undefined && value !== ""
              ? value
              : field.value || undefined
          } // ✅ shows placeholder when empty
          // @ts-ignore
          name={name}
          onChange={handleChange}
          options={props.mode ? options : [...options]}
          style={{
            fontFamily: "Poppins,sans-serif",
            width: "100%",
            maxHeight: Number(height),
            overflowX: "auto",
          }}
          suffixIcon={
            iconHide ? (
              ""
            ) : (
              <CaretDownOutlined style={{ color: "#808080", fontSize: 9 }} />
            )
          }
          size={size}
          onBlur={() => setFieldTouched(`${name}`, true)}
          disabled={disable}
          className={customStyle}
          tagRender={(tagProps) => {
            const isHexColor = /^#([0-9A-F]{3}){1,2}$/i.test(tagProps.value);
            if (!isHexColor) {
              return (
                <span
                  style={{
                    display: height ? "inline-block !important" : "",
                    height: height ? "50px !important" : "",
                    lineHeight: height ? "50px !important" : "",
                    padding: height ? "6px 10px" : "0 6px",
                    background: "#f0f0f0",
                    borderRadius: "4px",
                    marginRight: "4px",
                  }}
                >
                  {tagProps.label}
                  {tagProps.closable && (
                    <span
                      onClick={tagProps.onClose}
                      style={{ marginLeft: 5, cursor: "pointer", fontSize: 12 }}
                    >
                      ✕
                    </span>
                  )}
                </span>
              );
            }
            return (
              <span
                style={{
                  display: height ? "inline-block !important" : "",
                  alignItems: "center",
                  padding: height ? "10px" : "0 6px",
                  background: "#f0f0f0",
                  borderRadius: "4px",
                  marginRight: "4px",
                  height: height ? "50px !important" : "",
                  lineHeight: height ? "50px !important" : "",
                }}
              >
                {tagProps.label}
                <span
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    borderRadius: "50%",
                    backgroundColor: tagProps.value,
                    marginRight: 5,
                    marginLeft: 5,
                  }}
                />
                {tagProps.closable && (
                  <span
                    onClick={tagProps.onClose}
                    style={{ marginLeft: 5, cursor: "pointer", fontSize: 12 }}
                  >
                    ✕
                  </span>
                )}
              </span>
            );
          }}
          onInputKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "e") {
              const val = (e.target as HTMLInputElement).value;
              if (val.trim() === "") e.preventDefault();
            }
          }}
        />
      </ConfigProvider>

      <ErrorMessage name={name} component={StyledError} />
    </FieldContainer>
  );
};

export default SelectFieldWithSearch;
